import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { LANGUAGES } from '../../../constants';
import { isSupportedLang } from '../../../constants/languages';

export const useLanguage = () => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const isChangingRef = useRef(false);
    const [isLoading, setIsLoading] = useState(false);

    const languages = useMemo(() => Object.values(LANGUAGES) || [], []);
    const currentLanguage = useMemo(
        () => i18n.language || 'en',
        [i18n.language],
    );

    const removeAllLanguagePrefixes = useCallback((pathname) => {
        const pathSegments = pathname.split('/').filter(Boolean);
        let startIndex = 0;
        while (
            startIndex < pathSegments.length &&
            isSupportedLang(pathSegments[startIndex])
        ) {
            startIndex++;
        }

        if (startIndex >= pathSegments.length) {
            return '';
        }

        return `/${pathSegments.slice(startIndex).join('/')}`;
    }, []);

    const onChange = useCallback(
        async (e) => {
            if (isChangingRef.current || isLoading) {
                return;
            }

            const newLang = e.target.value;

            if (!newLang || !isSupportedLang(newLang)) {
                return;
            }

            if (newLang === currentLanguage) {
                return;
            }

            isChangingRef.current = true;
            setIsLoading(true);

            try {
                const pathWithoutLang = removeAllLanguagePrefixes(
                    location.pathname,
                );

                let newPath;
                if (pathWithoutLang === '') {
                    newPath = `/${newLang}/`;
                } else {
                    newPath = `/${newLang}${pathWithoutLang}`;
                    if (
                        !pathWithoutLang.includes('.') &&
                        !newPath.endsWith('/')
                    ) {
                        const lastSegment = pathWithoutLang.split('/').pop();
                        const isUUID =
                            /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i.test(
                                lastSegment,
                            );
                        const isNumericId = /^\d+$/.test(lastSegment);

                        if (!isUUID && !isNumericId) {
                            newPath += '/';
                        }
                    }
                }

                const queryAndHash = location.search + location.hash;
                if (queryAndHash) {
                    newPath += queryAndHash;
                }

                await i18n.changeLanguage(newLang);

                const preservedState = location.state;

                setTimeout(() => {
                    navigate(newPath, {
                        replace: true,
                        state: preservedState,
                    });
                }, 50);
            } catch (error) {
                console.error('Language change error:', error);
            } finally {
                setTimeout(() => {
                    isChangingRef.current = false;
                    setIsLoading(false);
                }, 200);
            }
        },
        [
            currentLanguage,
            navigate,
            location.pathname,
            location.search,
            location.hash,
            location.state,
            i18n,
            isLoading,
            removeAllLanguagePrefixes,
        ],
    );

    return {
        languages,
        onChange,
        currentLanguage,
        isLoading,
    };
};
